import {
  OrderIntakeOption,
  OrderRequest,
  OrderRequestGroupResponse
} from '../../../declarations/types'
import {SubmitHandler, UseFormReturn} from 'react-hook-form'
import {BulkOrderFormValues} from '../../../BulkOrdersFormValues'
import {trackSubmissionAnalytics} from '../../handlers/trackSubmissionAnalytics'
import {isEmpty} from 'lodash'
import {submitOrders} from '../../handlers/submitOrders'
import {validateDeliveryDates} from './validateDeliveryDates'
import {LoadType} from '../../../../App.constants'
import {ShippingType} from '../../../declarations/OrderIntake.enums'

interface UseDeliverSubmitParams {
  methods: UseFormReturn<BulkOrderFormValues>
  orders: OrderRequest[]
  replaceOrders: (orders: OrderRequest[]) => void
  selectedSite: OrderIntakeOption
  userProfileCountry: string | undefined
  userPrimaryOrganisationalUnit: string | undefined
  businessLineCode: string
  defaultBusinessLine: string
  quantityType: string
  orderIntakeCustomerReferenceField: boolean
  hasSiteContactNameAndPhone: boolean
  shouldUseV2: boolean
  isConfigurableSlots: boolean
  isAutoConfirmed: boolean
  language: string
  orderSessionEventId: string | undefined
  mutateAsyncV1: (params: {
    orderIntakePayload: any
    isConfigurableSlots: boolean
    isAutoConfirmed: boolean
  }) => Promise<any>
  mutateAsyncV2: (params: {orderIntakePayload: any}) => Promise<any>
  setDeliveries: (deliveries: OrderRequest[]) => void
  setPlaceOrderStatus: (status: boolean) => void
  setOrderSummaryDataArray: (dataArray: OrderRequestGroupResponse[]) => void
  setOrderSummary: (summary: any) => void
  setShowConfirmationStatusScreen: (show: boolean) => void
}

export const useDeliverSubmit = (params: UseDeliverSubmitParams) => {
  const {
    selectedSite,
    hasSiteContactNameAndPhone,
    setDeliveries,
    setPlaceOrderStatus,
    shouldUseV2,
    userProfileCountry,
    userPrimaryOrganisationalUnit,
    mutateAsyncV1,
    mutateAsyncV2,
    businessLineCode,
    defaultBusinessLine,
    isConfigurableSlots,
    isAutoConfirmed,
    setShowConfirmationStatusScreen,
    setOrderSummary,
    setOrderSummaryDataArray,
    quantityType,
    orderIntakeCustomerReferenceField,
    orderSessionEventId,
    language,
    replaceOrders,
    orders,
    methods
  } = params

  const onSubmit: SubmitHandler<BulkOrderFormValues> = (data) => {
    // Validate delivery dates before proceeding with submission
    const {hasUnavailableDates, validatedOrders} = validateDeliveryDates(orders, selectedSite)
    replaceOrders(validatedOrders)
    if (hasUnavailableDates) return

    const updatedDeliveries = data.orders.map<OrderRequest>((delivery) => ({
      ...delivery,
      payload: {
        ...delivery.payload,
        additionalDriverInfo: {
          ...delivery.payload.additionalDriverInfo
        },
        isSendingConfirmationEmailUnChecked: !data.isConfirmationMailChecked,
        contact: {
          ...data.contact,
          backupPhone:
            isEmpty(data.contact?.backupPhone) && hasSiteContactNameAndPhone
              ? data.contact?.mainPhone
              : data.contact?.backupPhone
        }
      }
    }))

    methods.setValue('isOrderRequestConfirmed', false)

    setDeliveries(updatedDeliveries)
    setPlaceOrderStatus(true)

    // Submitting orders is the last step, so we can use the updated deliveries with all the latest changes for analytics and submission
    submitOrders({
      updatedDeliveries,
      shouldUseV2,
      selectedSite,
      userProfileCountry,
      businessLineCode,
      userPrimaryOrganisationalUnit,
      mutateAsyncV1,
      mutateAsyncV2,
      isConfigurableSlots,
      isAutoConfirmed,
      setOrderSummaryDataArray,
      setOrderSummary,
      setPlaceOrderStatus,
      setShowConfirmationStatusScreen
    })

    // Track analytics after submission to ensure we have the most up-to-date data, including any changes made during submission
    trackSubmissionAnalytics({
      quantityType: quantityType,
      updatedDeliveries: updatedDeliveries,
      data: data,
      defaultBusinessLine: defaultBusinessLine,
      orderIntakeCustomerReferenceField: orderIntakeCustomerReferenceField,
      orders: orders,
      orderSessionEventId: orderSessionEventId ?? '',
      language: language,
      loadType: LoadType.NonSplit,
      shippingType: ShippingType.DELIVER,
      trackAlternativePhone: true
    })
  }

  return {onSubmit}
}
